import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Skeleton from "@mui/material/Skeleton";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatchApp } from "../../../lib/redux";
import { startSendGlobalAgentMessage } from "../../../actions/chat";
import { useTranslationApp } from "../../../lib/i18next";
import {
  GLOBAL_REPORT_AGENT_INSIGHTS,
  GLOBAL_REPORT_INSIGHTS_ERROR,
  GLOBAL_REPORT_INSIGHTS_QUESTION,
  GLOBAL_REPORT_NEW_INSIGHTS,
  GLOBAL_REPORT_NO_INSIGHTS,
} from "../../../i18n/keysTranslations";
import { BORDER_RADIUS } from "../../../utils/constants";

const SEVERITY_ORDER = ["positive", "warning", "critical"];

const SEVERITY_CONFIG = {
  critical: {
    icon: ErrorOutlineIcon,
    borderColor: "#f5c6cb",
    backgroundColor: "#fff5f5",
    iconColor: "#c5221f",
  },
  warning: {
    icon: LightbulbOutlinedIcon,
    borderColor: "#ffe082",
    backgroundColor: "#fffbf0",
    iconColor: "#e37400",
  },
  positive: {
    icon: TrendingUpIcon,
    borderColor: "#b7e1cd",
    backgroundColor: "#f6fff8",
    iconColor: "#137333",
  },
};

const groupInsightsBySeverity = (insights) => {
  const grouped = {
    positive: [],
    warning: [],
    critical: [],
  };

  insights.forEach((insight) => {
    const severity = grouped[insight.severity] ? insight.severity : "warning";
    grouped[severity].push(insight);
  });

  return grouped;
};

const InsightCard = ({ insight }) => {
  const config = SEVERITY_CONFIG[insight.severity] || SEVERITY_CONFIG.warning;
  const Icon = config.icon;

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        p: 1.5,
        borderRadius: `${BORDER_RADIUS * 2}px`,
        border: `1px solid ${config.borderColor}`,
        backgroundColor: config.backgroundColor,
      }}
    >
      <Icon sx={{ color: config.iconColor, mt: 0.25, flexShrink: 0 }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {insight.title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {insight.description}
        </Typography>
      </Box>
    </Box>
  );
};

const GlobalReportInsightsPanel = ({
  globalReportContext,
  agentRequestKey,
  isReady = false,
  onInsightsGenerated = () => {},
  onAiSettledChange = () => {},
}) => {
  const { t, i18n } = useTranslationApp();
  const dispatch = useDispatchApp();
  const [insights, setInsights] = useState([]);
  const [isLoadingInsights, setIsLoadingInsights] = useState(false);
  const [insightsError, setInsightsError] = useState(false);
  const fetchIdRef = useRef(0);
  const lastFetchedKeyRef = useRef(null);

  useEffect(() => {
    if (!isReady || !globalReportContext || !agentRequestKey) {
      onAiSettledChange(false);
      return;
    }

    const agentFetchKey = `${agentRequestKey}|${i18n.language}`;
    if (lastFetchedKeyRef.current === agentFetchKey) {
      onAiSettledChange(true);
      return;
    }
    lastFetchedKeyRef.current = agentFetchKey;

    const fetchId = ++fetchIdRef.current;
    setIsLoadingInsights(true);
    setInsightsError(false);
    setInsights([]);

    dispatch(
      startSendGlobalAgentMessage({
        messages: [
          {
            role: "user",
            content: t(GLOBAL_REPORT_INSIGHTS_QUESTION),
          },
        ],
        globalReportContext: {
          ...globalReportContext,
          // Slim metrics tool payloads for auto-insights only; chat stays full
          metricsProjection: "kpi",
        },
      })
    )
      .then((response) => {
        if (fetchId !== fetchIdRef.current) {
          return;
        }

        if (response?.ok) {
          const nextInsights = response.data?.[0]?.insights || [];
          setInsights(nextInsights);
          onInsightsGenerated(nextInsights);
        } else {
          setInsightsError(true);
          onInsightsGenerated([]);
        }
      })
      .catch((error) => {
        console.error("Error generating global report insights:", error);
        if (fetchId === fetchIdRef.current) {
          setInsightsError(true);
          onInsightsGenerated([]);
        }
      })
      .finally(() => {
        if (fetchId === fetchIdRef.current) {
          setIsLoadingInsights(false);
          onAiSettledChange(true);
        }
      });
  }, [
    agentRequestKey,
    dispatch,
    globalReportContext,
    i18n.language,
    isReady,
    onAiSettledChange,
    onInsightsGenerated,
    t,
  ]);

  useEffect(() => {
    return () => onAiSettledChange(false);
  }, [onAiSettledChange]);

  const hasInsights = insights.length > 0;

  const insightsBySeverity = useMemo(
    () => groupInsightsBySeverity(insights),
    [insights]
  );

  const severityColumns = useMemo(
    () =>
      SEVERITY_ORDER.map((severity) => ({
        severity,
        items: insightsBySeverity[severity],
      })).filter((column) => column.items.length > 0),
    [insightsBySeverity]
  );

  if (!globalReportContext) {
    return null;
  }

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t(GLOBAL_REPORT_AGENT_INSIGHTS)}
        </Typography>
        {!isLoadingInsights && isReady && hasInsights && (
          <Chip
            label={t(GLOBAL_REPORT_NEW_INSIGHTS, {
              count: insights.length,
            })}
            size="small"
            color="primary"
            sx={{ fontWeight: 600 }}
          />
        )}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "repeat(2, 1fr)",
            lg: "repeat(3, 1fr)",
          },
          gap: 1.5,
          width: "100%",
        }}
      >
        {isLoadingInsights || !isReady ? (
          Array.from({ length: 3 }).map((_, index) => (
            <Box
              key={`insight-skeleton-column-${index}`}
              sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
            >
              <Skeleton
                variant="rounded"
                height={72}
                sx={{ borderRadius: `${BORDER_RADIUS * 2}px` }}
              />
              <Skeleton
                variant="rounded"
                height={72}
                sx={{ borderRadius: `${BORDER_RADIUS * 2}px` }}
              />
            </Box>
          ))
        ) : insightsError ? (
          <Typography
            variant="body2"
            color="error"
            sx={{ gridColumn: "1 / -1" }}
          >
            {t(GLOBAL_REPORT_INSIGHTS_ERROR)}
          </Typography>
        ) : hasInsights ? (
          severityColumns.map(({ severity, items }) => (
            <Box
              key={severity}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                minWidth: 0,
              }}
            >
              {items.map((insight) => (
                <InsightCard key={insight.id} insight={insight} />
              ))}
            </Box>
          ))
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ gridColumn: "1 / -1" }}
          >
            {t(GLOBAL_REPORT_NO_INSIGHTS)}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default GlobalReportInsightsPanel;
